import { useNursery, useRack, useRacks, useTank, useTanks } from "@/hooks";
import { Heading, Breadcrumb, Container, Flex, Skeleton, Menu, createListCollection, ListCollection, Portal } from "@chakra-ui/react";
import { PropsWithChildren, useMemo } from "react";
import { LuChevronDown, LuCheck } from "react-icons/lu";
import { Link, useParams } from "react-router-dom";

interface PageTitleProps extends PropsWithChildren {
  isPending?: boolean;
  nurseryName?: string;
  tankName?: string;
  rackName?: string;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

interface BreadcrumbMenuItemProps<T> {
  children: React.ReactNode
  collection: ListCollection<T>
  currentValue: string
  getHref: (value: string) => string
}

const BreadcrumbMenuItem = <T,>(props: BreadcrumbMenuItemProps<T>) => {
  const { children, collection, currentValue, getHref } = props;
  return (
    <Breadcrumb.Item>
      <Menu.Root>
        <Menu.Trigger border="1px solid" borderColor="border" px="2" py="0.5" bg="bg" asChild>{children}</Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content>
              {collection.items.map((item) => {
                const value = collection.getItemValue(item) ?? "";
                return (
                  <Menu.Item key={value} value={value} asChild>
                    <Link to={getHref(value)}>
                      <LuCheck
                        visibility={value === currentValue ? "visible" : "hidden"}
                      />
                      {collection.stringifyItem(item)}
                    </Link>
                  </Menu.Item>
                );
              })}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Breadcrumb.Item>
  );
};

function PageTitle({
  isPending,
  nurseryName,
  tankName,
  rackName,
  children,
}: PageTitleProps) {
  const params = useParams();
  const { nurseryId, tankId, rackId } = params;

  const { data: nurseryData } = useNursery(nurseryId!, !!nurseryId && !nurseryName);
  const { data: tankData } = useTank(nurseryId!, tankId!, !!nurseryId && !!tankId && !tankName);
  const { data: rackData } = useRack(
    nurseryId!,
    tankId!,
    rackId!,
    !!nurseryId && !!tankId && !!rackId && !rackName
  );

  const tanks = useTanks(nurseryId!, !!nurseryId && !!tankId && !rackId);
  const tanksList = useMemo(() => {
    return createListCollection({
      items: tanks?.data?.results ?? [],
      itemToString: (tank) => tank.name,
      itemToValue: (tank) => tank.id.toString(),
    });
  }, [tanks.data]);

  const racks = useRacks(nurseryId!, tankId!, !!nurseryId && !!tankId && !!rackId);
  const racksList = useMemo(() => {
    return createListCollection({
      items: racks?.data?.results ?? [],
      itemToString: (rack) => rack.name,
      itemToValue: (rack) => rack.id.toString(),
    });
  }, [racks.data]);

  if (!nurseryId) {
    return (
      <Container p={4} maxW={{ sm: "7xl", "2xl": "8xl" }}>
        <Heading as="h2" fontWeight="bold" size="lg">
          {children}
        </Heading>
      </Container>
    );
  }

  const currentKey = rackId ? "rack" : tankId ? "tank" : "nursery";

  const getLabel = (key: string, value: string | undefined) => {
    switch (key) {
    case "nurseryId":
      return currentKey === "nursery"
        ? "Nursery Dashboard"
        : nurseryName || nurseryData?.name || `Nursery ${value}`;
    case "tankId":
      return `Tank ${tankName || tankData?.name || value}`;
    case "rackId":
      return `Rack ${rackName || rackData?.name || value}`;
    default:
      return `${capitalize(key.replace(/Id$/, ""))} ${value}`;
    }
  };

  const renderActiveBreadcrumbItem = (label: string) => {
    if (currentKey === "nursery") {
      return <Breadcrumb.Link>{label}</Breadcrumb.Link>;
    }

    const trigger = (
      <Breadcrumb.Link
        as="button"
        textTransform="uppercase"
        fontWeight="600"
        letterSpacing="0.75px"
        color="gray.500"
        _hover={{
          cursor: "pointer"
        }}
      >
        {label}
        <LuChevronDown />
      </Breadcrumb.Link>
    );

    switch (currentKey) {
    case "tank":
      return (
        <BreadcrumbMenuItem
          collection={tanksList}
          currentValue={tankId!}
          getHref={(value) => `/nurseries/${nurseryId}/tanks/${value}/`}
        >
          {trigger}
        </BreadcrumbMenuItem>
      );
    case "rack":
      return (
        <BreadcrumbMenuItem
          collection={racksList}
          currentValue={rackId!}
          getHref={(value) => `/nurseries/${nurseryId}/tanks/${tankId}/racks/${value}/`}
        >
          {trigger}
        </BreadcrumbMenuItem>
      );
    }
  };

  const breadcrumbItems = Object.entries(params).flatMap(([key, value], index, array) => {
    const isLast = index === array.length - 1;
    const label = getLabel(key, value);
    const linkPath = array
      .slice(0, index + 1)
      .map(([k, v]) => `${k === "nurseryId" ? "nurseries" : k === "tankId" ? "tanks" : "racks"}/${v}`)
      .join("/");

    return [
      <Breadcrumb.Item key={key}>
        {isLast ? (
          renderActiveBreadcrumbItem(label)
        ) : (
          <Breadcrumb.Link asChild>
            <Link to={`/${linkPath}`}>{label}</Link>
          </Breadcrumb.Link>
        )}
      </Breadcrumb.Item>,
      !isLast && <Breadcrumb.Separator key={`separator-${key}`} />,
    ];
  });

  const getHeading = () => {
    switch (currentKey) {
    case "nursery":
      return nurseryName || nurseryData?.name || "Nursery Insights";
    case "tank":
      return tankName || tankData?.name || "Tank Insights";
    case "rack":
      return rackName || rackData?.name || "Rack Insights";
    default:
      return `${capitalize(currentKey)} Insights`;
    }
  };

  return (
    <Container p={4} maxW={{ sm: "7xl", "2xl": "8xl" }} display="flex" justifyContent="space-between" alignItems="flex-end">
      <Flex flexDir="column" gap="1">
        <Skeleton loading={isPending} alignSelf="flex-start">
          <Breadcrumb.Root
            size="sm"
            textTransform="uppercase"
            fontWeight="600"
            letterSpacing="0.75px"
            color="gray.500"
          >
            <Breadcrumb.List flexWrap={{ base: "wrap", sm: "nowrap"}}>{breadcrumbItems}</Breadcrumb.List>
          </Breadcrumb.Root>
        </Skeleton>
        <Skeleton loading={isPending} alignSelf="flex-start">
          <Heading size="3xl">{getHeading()}</Heading>
        </Skeleton>
        {currentKey !== "nursery" && (
          <Skeleton loading={isPending} alignSelf="flex-start">
            <Heading size="sm" as="h3" letterSpacing="0.5px" color="gray.700">
              {`${capitalize(currentKey)} Insights`}
            </Heading>
          </Skeleton>
        )}
      </Flex>
      {children}
    </Container>
  );
}

export default PageTitle;
